import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Package2, Menu, X, Sparkles, FileText, Home } from 'lucide-react';
import { Button } from '@/components/ui/button';
import LanguageSelector from '@/components/LanguageSelector';

/**
 * Public navigation bar for unauthenticated pages (Designs, Blog)
 * Includes mobile hamburger menu with Designs and Blog links
 */
export default function PublicNavbar() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();

  const isDesignsPage = location.pathname.startsWith('/designs');
  const isBlogPage = location.pathname.startsWith('/blog');

  return (
    <nav className="bg-white/80 backdrop-blur-md border-b border-stone-100 sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <div className="flex justify-between items-center">
          {/* Logo */}
          <div 
            className="flex items-center space-x-2 cursor-pointer flex-shrink-0" 
            onClick={() => navigate('/')}
          >
            <Package2 className="h-8 w-8 text-primary" />
            <h1 className="text-2xl font-heading font-extrabold text-foreground tracking-tight">Even Rounds</h1>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-4">
            <LanguageSelector variant="ghost" />
            <Button 
              variant={isDesignsPage ? "default" : "ghost"}
              onClick={() => navigate('/designs')} 
              className="rounded-full"
            >
              <Sparkles className="h-4 w-4 mr-2" />
              {t('nav.designs')}
            </Button>
            <Button 
              variant={isBlogPage ? "default" : "ghost"}
              onClick={() => navigate('/blog')} 
              className="rounded-full"
            >
              <FileText className="h-4 w-4 mr-2" />
              {t('nav.blog')}
            </Button>
            <Button 
              variant="outline" 
              onClick={() => navigate('/')} 
              className="rounded-full"
            >
              <Home className="h-4 w-4 mr-2" />
              {t('nav.home')}
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <div className="flex md:hidden items-center space-x-2">
            <LanguageSelector variant="ghost" />
            <button
              data-testid="public-mobile-menu-button"
              className="p-2 rounded-lg hover:bg-stone-100 transition-colors"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
            >
              {mobileMenuOpen ? (
                <X className="h-6 w-6 text-stone-600" />
              ) : (
                <Menu className="h-6 w-6 text-stone-600" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden border-t border-stone-100 bg-white">
          <div className="px-4 py-4 space-y-2">
            <button
              onClick={() => { navigate('/designs'); setMobileMenuOpen(false); }}
              className={`w-full flex items-center space-x-3 font-medium py-3 px-4 rounded-xl transition-colors ${
                isDesignsPage 
                  ? 'bg-primary text-primary-foreground' 
                  : 'text-stone-600 hover:bg-stone-50 hover:text-primary'
              }`}
            >
              <Sparkles className="h-5 w-5" />
              <span>{t('nav.designs')}</span>
            </button>
            <button
              onClick={() => { navigate('/blog'); setMobileMenuOpen(false); }}
              className={`w-full flex items-center space-x-3 font-medium py-3 px-4 rounded-xl transition-colors ${
                isBlogPage 
                  ? 'bg-primary text-primary-foreground' 
                  : 'text-stone-600 hover:bg-stone-50 hover:text-primary'
              }`}
            >
              <FileText className="h-5 w-5" />
              <span>{t('nav.blog')}</span>
            </button>
            <button
              onClick={() => { navigate('/'); setMobileMenuOpen(false); }}
              className="w-full flex items-center space-x-3 text-stone-600 hover:bg-stone-50 hover:text-primary font-medium py-3 px-4 rounded-xl transition-colors"
            >
              <Home className="h-5 w-5" />
              <span>{t('nav.home')}</span>
            </button>
          </div>
        </div>
      )}
    </nav>
  );
}
